import { ref, onMounted, onUnmounted, type Ref } from "vue";

export interface MarqueeSelectionOptions {
  containerRef: Ref<HTMLElement | null>;
  itemSelector: string;
  itemIdAttribute: string;
  onSelectionChange: (ids: number[], isSelecting: boolean) => void;
  onSelectionEnd: (ids: number[]) => void;
}

export function useMarqueeSelection(options: MarqueeSelectionOptions) {
  const {
    containerRef,
    itemSelector,
    itemIdAttribute,
    onSelectionChange,
    onSelectionEnd,
  } = options;

  const isSelecting = ref(false);
  const selectionRect = ref({ x: 0, y: 0, width: 0, height: 0 });
  const startPoint = ref({ x: 0, y: 0 });
  const hoveredIds = ref<Set<number>>(new Set());

  // Minimum drag distance before starting selection (to avoid accidental selections)
  const MIN_DRAG_DISTANCE = 5;
  let hasDraggedEnough = false;
  let animationFrameId: number | null = null;

  const getScrollOffset = () => {
    const container = containerRef.value;
    if (!container) return { scrollLeft: 0, scrollTop: 0 };
    return {
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
  };

  const getContainerRect = () => {
    const container = containerRef.value;
    if (!container) return null;
    return container.getBoundingClientRect();
  };

  const updateSelectionRect = (clientX: number, clientY: number) => {
    const containerRect = getContainerRect();
    if (!containerRect) return;

    const scroll = getScrollOffset();

    // Calculate position relative to container content (including scroll)
    const currentX = clientX - containerRect.left + scroll.scrollLeft;
    const currentY = clientY - containerRect.top + scroll.scrollTop;

    // Calculate rectangle dimensions
    const x = Math.min(startPoint.value.x, currentX);
    const y = Math.min(startPoint.value.y, currentY);
    const width = Math.abs(currentX - startPoint.value.x);
    const height = Math.abs(currentY - startPoint.value.y);

    selectionRect.value = { x, y, width, height };
  };

  const getItemsInSelection = (): number[] => {
    const container = containerRef.value;
    if (!container) return [];

    const items = container.querySelectorAll(itemSelector);
    const selectedIds: number[] = [];
    const scroll = getScrollOffset();
    const containerRect = getContainerRect();
    if (!containerRect) return [];

    const rect = selectionRect.value;

    items.forEach((item) => {
      const itemRect = item.getBoundingClientRect();

      // Convert item rect to container content coordinates
      const itemLeft = itemRect.left - containerRect.left + scroll.scrollLeft;
      const itemTop = itemRect.top - containerRect.top + scroll.scrollTop;
      const itemRight = itemLeft + itemRect.width;
      const itemBottom = itemTop + itemRect.height;

      // Check intersection
      const intersects =
        itemLeft < rect.x + rect.width &&
        itemRight > rect.x &&
        itemTop < rect.y + rect.height &&
        itemBottom > rect.y;

      if (intersects) {
        const idStr = item.getAttribute(itemIdAttribute);
        if (idStr) {
          const id = parseInt(idStr, 10);
          if (!isNaN(id)) {
            selectedIds.push(id);
          }
        }
      }
    });

    return selectedIds;
  };

  const handleMouseDown = (event: MouseEvent) => {
    // Only start on left click
    if (event.button !== 0) return;

    // Don't start if clicking on interactive elements or gallery items
    const target = event.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest("input") ||
      target.closest('[role="button"]') ||
      target.closest(".checkbox-indicator") ||
      target.closest(".el-dropdown") ||
      target.closest(".clickable-description") ||
      target.closest("[data-gallery-item-id]")
    ) {
      return;
    }

    const containerRect = getContainerRect();
    if (!containerRect) return;

    const scroll = getScrollOffset();
    const x = event.clientX - containerRect.left + scroll.scrollLeft;
    const y = event.clientY - containerRect.top + scroll.scrollTop;

    startPoint.value = { x, y };
    selectionRect.value = { x, y, width: 0, height: 0 };
    hasDraggedEnough = false;

    // Add global listeners
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (!hasDraggedEnough) {
      const containerRect = getContainerRect();
      if (!containerRect) return;

      const scroll = getScrollOffset();
      const currentX = event.clientX - containerRect.left + scroll.scrollLeft;
      const currentY = event.clientY - containerRect.top + scroll.scrollTop;

      const distance = Math.sqrt(
        Math.pow(currentX - startPoint.value.x, 2) +
          Math.pow(currentY - startPoint.value.y, 2)
      );

      if (distance < MIN_DRAG_DISTANCE) {
        return;
      }

      hasDraggedEnough = true;
      isSelecting.value = true;
    }

    // Use requestAnimationFrame for smooth updates
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }

    animationFrameId = requestAnimationFrame(() => {
      updateSelectionRect(event.clientX, event.clientY);

      const ids = getItemsInSelection();
      const newHoveredIds = new Set(ids);

      // Only update if changed
      if (
        ids.length !== hoveredIds.value.size ||
        ids.some((id) => !hoveredIds.value.has(id))
      ) {
        hoveredIds.value = newHoveredIds;
        onSelectionChange(ids, true);
      }
    });
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    if (isSelecting.value) {
      const ids = getItemsInSelection();
      onSelectionEnd(ids);
    }

    isSelecting.value = false;
    hoveredIds.value = new Set();
    selectionRect.value = { x: 0, y: 0, width: 0, height: 0 };
  };

  const isHovered = (id: number) => hoveredIds.value.has(id);

  onMounted(() => {
    const container = containerRef.value;
    if (container) {
      container.addEventListener("mousedown", handleMouseDown);
    }
  });

  onUnmounted(() => {
    const container = containerRef.value;
    if (container) {
      container.removeEventListener("mousedown", handleMouseDown);
    }
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }
  });

  return {
    isSelecting,
    selectionRect,
    hoveredIds,
    isHovered,
  };
}
